import { useEffect, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ACCESS_ROLES, NAV } from 'src/config';
import { ACCOUNT_BILLING_INFORMATION_PATH } from 'src/routes/config';
import { useAuthentication } from 'src/features/authentication/context';
import {
  useGetBillingStatusQuery,
  getBillingBlockedMessage,
  BILLING_INFORMATION_STATUS_QUERY_KEY,
} from 'src/hooks/reactQueryApiHooks';
import { LocalStorageService } from 'src/features/authentication/utils/LocalStorageService';

const DISMISS_DURATION_MS = 1000 * 60 * 60 * 24; // 24 hours
// const DISMISS_DURATION_MS = 1000 * 5; // dev

const RECHECK_INTERVAL_MS = 1000 * 60 * 15; // 15 min
// const RECHECK_INTERVAL_MS = 1000 * 3; // dev

const getStorageKey = (customerId: string) => `billing_alert_dismissed_${customerId}`;

const isDismissalActive = (customerId: string): boolean => {
  if (customerId === '') {
    return true;
  }

  try {
    const rawTimestamp = LocalStorageService.getRaw(getStorageKey(customerId));

    if (!rawTimestamp) {
      return false;
    }

    const active = Date.now() - Number(rawTimestamp) < DISMISS_DURATION_MS;

    if (!active) {
      LocalStorageService.remove(getStorageKey(customerId));
    }

    return active;
  } catch {
    return false;
  }
};

export const BillingAlert = () => {
  const {
    getCurrentRole,
    customerId: { value: customerId },
    isBillingAdminForCustomer,
  } = useAuthentication();
  const queryClient = useQueryClient();

  const validCustomerId = customerId ?? '';
  const currentRole = getCurrentRole(validCustomerId, null);
  const canGoToBilling = isBillingAdminForCustomer(validCustomerId);
  const canSeeBillingAlert = canGoToBilling || currentRole === ACCESS_ROLES.ACCOUNT_ADMIN;

  const { billingInformationStatus, dataUpdatedAt } = useGetBillingStatusQuery({
    customerId: validCustomerId,
    enabled: canSeeBillingAlert,
  });

  const [isDismissed, setIsDismissed] = useState<boolean>(() => isDismissalActive(validCustomerId));

  useEffect(() => {
    if (validCustomerId === '') {
      return;
    }

    setIsDismissed(isDismissalActive(validCustomerId));
  }, [validCustomerId, dataUpdatedAt]);

  const handleDismiss = () => {
    LocalStorageService.set(getStorageKey(validCustomerId), String(Date.now()));
    setIsDismissed(true);
  };

  useEffect(() => {
    if (!validCustomerId) {
      return;
    }

    if (billingInformationStatus?.billingConfigured !== false) {
      return;
    }

    const interval = setInterval(() => {
      const active = isDismissalActive(validCustomerId);

      if (!active) {
        queryClient.refetchQueries({
          queryKey: [BILLING_INFORMATION_STATUS_QUERY_KEY, validCustomerId],
        });
      }
    }, RECHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [validCustomerId, billingInformationStatus?.billingConfigured, queryClient]);

  const billingNotConfigured =
    !!billingInformationStatus && !billingInformationStatus.billingConfigured;

  const showAlert = billingNotConfigured && !isDismissed && canSeeBillingAlert;

  if (!showAlert) return null;

  const alertMessage = getBillingBlockedMessage(billingInformationStatus?.hasBillingAdmin);

  return (
    <Alert
      severity="warning"
      action={
        <>
          <Button color="inherit" size="small" onClick={handleDismiss} sx={{ mr: 1 }}>
            Dismiss
          </Button>
          {canGoToBilling && (
            <Button
              component={Link}
              to={`${ACCOUNT_BILLING_INFORMATION_PATH}?customerId=${validCustomerId}`}
              color="inherit"
              size="small"
              variant="outlined"
              sx={{ borderColor: 'warning.dark', whiteSpace: 'nowrap' }}
            >
              Go To Billing
            </Button>
          )}
        </>
      }
      sx={(theme) => ({
        position: 'fixed',
        top: 20,
        left: { xs: '50%', md: `calc(50% + ${NAV.W_SIDE_BAR / 2}px)` },
        transform: 'translateX(-50%)',
        width: { xs: 'calc(100% - 32px)', md: `calc(100vw - ${NAV.W_SIDE_BAR}px - 32px)` },
        maxWidth: 'md',
        zIndex: 'appBar',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
        justifyContent: 'center',
        alignItems: 'center',
        '& .MuiAlert-message': { flex: '0 1 auto' },
        '& .MuiAlert-action': {
          alignItems: 'center',
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
          },
        },
      })}
    >
      {alertMessage}
    </Alert>
  );
};
